import en from '../locales/en.json';

export type I18nReader = {
  t(key: string, options?: Record<string, unknown>): string;
};

/** react-i18next's `getI18n`, once the browser's copy has been pulled in. */
let clientGetI18n: (() => I18nReader | undefined) | undefined;
let esmImportStarted = false;

/**
 * The browser's provider-owned i18next instance, resolved SYNCHRONOUSLY and
 * without dragging `react-i18next` into the server graph.
 *
 * Three constraints collide here:
 * <ul>
 *   <li>A static import is out: `react-i18next` builds its React context at
 *       module evaluation, and this module is server-safe — the RSC runtime
 *       would evaluate that context for code that only ever needs the shipped
 *       English fallback.</li>
 *   <li>`require()` alone is out: the admin is a native-ESM Vite bundle with
 *       no CommonJS shim, so it throws `ReferenceError: require is not
 *       defined` — which is exactly what it did, crashing every icon-picker
 *       field ({@code x-icon-picker}) in the page designer.</li>
 *   <li>`await import()` alone is out: every caller returns a string
 *       synchronously.</li>
 * </ul>
 *
 * So: `require` where a CJS loader actually exists (the portal's webpack
 * client bundle, where it is synchronous and has no first-frame gap), and a
 * dynamic import everywhere else — fired once at module evaluation, below, so
 * it has settled off the module cache long before any chrome or picker is on
 * screen. Until it does, callers get the same shipped English they get on the
 * server, never a crash.
 */
export function resolveClientI18n(): I18nReader | undefined {
  if (typeof window === 'undefined') return undefined;

  if (!clientGetI18n) {
    if (typeof require === 'function') {
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      clientGetI18n = (require('react-i18next') as { getI18n: () => I18nReader | undefined })
        .getI18n;
    } else if (!esmImportStarted) {
      esmImportStarted = true;
      void import('react-i18next').then((m) => {
        clientGetI18n = m.getI18n as unknown as () => I18nReader | undefined;
      });
    }
  }

  return clientGetI18n?.();
}

// Warm the resolver at module evaluation so the ESM path is already primed on
// the first synchronous read. No-op on the server.
resolveClientI18n();

/** i18next namespace this package's shipped catalogue is published under.
 *  Mirrors {@code locales/index.ts#NS}; kept as a literal so this module
 *  does not import the locales barrel (which would be a cycle). */
const NS = 'site-builder-runtime';

/** Walk a dotted key into the shipped English catalogue. Plural keys are
 *  stored as {@code …_one} / {@code …_other}, so a bare miss retries the
 *  {@code _other} form before giving up and echoing the key. */
function shippedEnglish(key: string): string {
  for (const candidate of [key, `${key}_other`]) {
    let node: unknown = en;
    for (const part of candidate.split('.')) {
      if (node == null || typeof node !== 'object') {
        node = undefined;
        break;
      }
      node = (node as Record<string, unknown>)[part];
    }
    if (typeof node === 'string') return node;
  }
  return key;
}

/**
 * Read one string from the package's shipped i18next catalogue, outside a
 * React component.
 *
 * <p>The chrome renderers, the system primitives and the icon catalogue are
 * plain modules and non-client components — a hook is either impossible or
 * would crash them when a host renders them on the server. This resolves the
 * key at CALL time (never at module eval, which would freeze the boot
 * language into a constant) and soft-fails to the package's own shipped
 * English when no i18next instance is mounted or the namespace was never
 * registered by the host.
 *
 * <p>Client components use {@code useTranslation(NS)} instead — a hook
 * re-renders on a language change; this does not.
 */
export function sbText(key: string, values?: Record<string, unknown>): string {
  const fallback = shippedEnglish(key);
  const i18n = resolveClientI18n();
  if (!i18n) return fallback;
  return i18n.t(key, { ns: NS, defaultValue: fallback, ...values });
}
